import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import formatDate from '../../components/formatDate';
import {
  getPulseErrorMessage,
  getPulsePayload,
  isPulseApiSuccess,
  listPulseCompetitions,
  patchPulseCompetition,
} from '../../services/pulseApi';
import { competitionFlagOn, formatNumber, parseCompetitionsPayload } from './pulseUtils';

function PulseCompetitions() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [competitions, setCompetitions] = useState([]);
  const [search, setSearch] = useState('');
  const [toggling, setToggling] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const loadCompetitions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await listPulseCompetitions();
      if (!isPulseApiSuccess(result)) {
        setError(getPulseErrorMessage(result, 'Failed to load Pulse competitions.'));
        setCompetitions([]);
        return;
      }
      setCompetitions(parseCompetitionsPayload(getPulsePayload(result)));
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load Pulse competitions.');
      setCompetitions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompetitions();
  }, [loadCompetitions]);

  const handleToggle = async (comp, field, value) => {
    const cid = comp.competition_cid;
    const key = `${cid}:${field}`;
    const previous = competitionFlagOn(comp[field]);
    setToggling(key);
    setCompetitions((prev) =>
      prev.map((c) => (c.competition_cid === cid ? { ...c, [field]: value } : c))
    );

    try {
      const result = await patchPulseCompetition(cid, { [field]: value });
      if (!isPulseApiSuccess(result)) {
        setCompetitions((prev) =>
          prev.map((c) => (c.competition_cid === cid ? { ...c, [field]: previous } : c))
        );
        setError(getPulseErrorMessage(result, 'Failed to update competition Pulse flags.'));
        return;
      }
      const payload = getPulsePayload(result);
      const updated = payload?.competition || payload;
      if (updated && typeof updated === 'object' && updated.competition_cid != null) {
        setCompetitions((prev) =>
          prev.map((c) => (c.competition_cid === cid ? { ...c, ...updated } : c))
        );
      }
      setSnackbar({
        open: true,
        message: 'Saved. Off Pulse stops new generation for this competition’s fixtures.',
      });
    } catch (toggleError) {
      setCompetitions((prev) =>
        prev.map((c) => (c.competition_cid === cid ? { ...c, [field]: previous } : c))
      );
      setError(toggleError.message || 'Failed to update competition Pulse flags.');
    } finally {
      setToggling('');
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return competitions;
    return competitions.filter((c) => {
      const hay = [c.title, c.abbr, c.season, c.status, c.competition_cid]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [competitions, search]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Competition enable</Typography>
        <IconButton onClick={loadCompetitions} disabled={loading} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Competitions with fixtures in a ~14-day-past / 30-day-ahead window (plus any already toggled).
        Missing settings row = <strong>on</strong> (opt-out). Match-level toggles still apply —{' '}
        <strong>both</strong> gates must allow generation. Turning competition Pulse{' '}
        <strong>Off</strong> stops new auto-enable / Instinct / Knowledge / Fan Wars / Ratings for
        that competition’s fixtures. Existing cards stay — expire them from Cards if needed. Needs
        migrations <code>203_pulse_competition_settings.sql</code> and{' '}
        <code>216_pulse_fanwars_ratings_toggles.sql</code>.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Title, abbr, cid"
          InputProps={{ endAdornment: <SearchIcon fontSize="small" color="action" /> }}
          sx={{ minWidth: 260 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Competition</TableCell>
                <TableCell>Season</TableCell>
                <TableCell>Window</TableCell>
                <TableCell align="right">Matches</TableCell>
                <TableCell title="Master switch for all fixtures">Pulse</TableCell>
                <TableCell title="Competition Instinct gate">Instinct</TableCell>
                <TableCell title="Competition Knowledge gate">Knowledge</TableCell>
                <TableCell title="Competition Pulse Fan Wars gate (default on)">Fan Wars</TableCell>
                <TableCell title="Competition Match Rating gate (default on)">Ratings</TableCell>
                <TableCell align="right">Fixtures</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No competitions in the Pulse window.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((comp) => {
                  const pulseOn = competitionFlagOn(comp.pulse_enabled);
                  const instinctOn = competitionFlagOn(comp.instinct_enabled);
                  const knowledgeOn = competitionFlagOn(comp.knowledge_enabled);
                  const fanwarsOn = competitionFlagOn(comp.fanwars_enabled);
                  const ratingsOn = competitionFlagOn(comp.ratings_enabled);
                  return (
                    <TableRow
                      key={comp.competition_cid}
                      hover
                      sx={{ opacity: pulseOn ? 1 : 0.7 }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {comp.title || comp.abbr || `CID ${comp.competition_cid}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          CID {comp.competition_cid}
                          {comp.abbr ? ` · ${comp.abbr}` : ''}
                          {comp.status ? ` · ${comp.status}` : ''}
                        </Typography>
                        {!pulseOn && (
                          <Box sx={{ mt: 0.5 }}>
                            <Chip size="small" color="warning" label="Blocked for fixtures" />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{comp.season || '—'}</TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block">
                          {comp.date_start ? formatDate(comp.date_start) : '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          → {comp.date_end ? formatDate(comp.date_end) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatNumber(comp.matches_in_window)}</TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={pulseOn}
                          disabled={toggling === `${comp.competition_cid}:pulse_enabled`}
                          onChange={(e) => handleToggle(comp, 'pulse_enabled', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={instinctOn}
                          disabled={toggling === `${comp.competition_cid}:instinct_enabled`}
                          onChange={(e) => handleToggle(comp, 'instinct_enabled', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={knowledgeOn}
                          disabled={toggling === `${comp.competition_cid}:knowledge_enabled`}
                          onChange={(e) => handleToggle(comp, 'knowledge_enabled', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={fanwarsOn}
                          disabled={toggling === `${comp.competition_cid}:fanwars_enabled`}
                          onChange={(e) => handleToggle(comp, 'fanwars_enabled', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={ratingsOn}
                          disabled={toggling === `${comp.competition_cid}:ratings_enabled`}
                          onChange={(e) => handleToggle(comp, 'ratings_enabled', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          component={Link}
                          to="/admin/pulse/matches"
                          size="small"
                        >
                          Matches
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
}

export default PulseCompetitions;
